import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { BoundaryEditAction } from '~/boundary/editMode/boundaryEditModal/boundaryEditModal.helpers';
import { QUICK_EDIT_BOUNDARY_COUNT_THRESHOLD } from '~/boundary/editMode/boundaryEditModal/constants';
import { useModal } from '~/modal/useModal.hook';
import { getBoundaryStyle } from '~/sidebar/sidebarApps/mapTools/boundary/boundaryItem.helpers';
import { useBoundaryEditPolygon } from '~/sidebar/sidebarApps/mapTools/boundary/edit/useBoundaryEditPolygon';
import { useBoundaryItems } from '~/sidebar/sidebarApps/mapTools/boundary/hooks/useBoundaryItems';
import { useBoundarySelectEditCustomBoundary } from '~/sidebar/sidebarApps/mapTools/boundary/loadBoundaryPane/createCustomBoundaryPane/useBoundarySelectEditCustomBoundary';
import { useGetBoundaryGroup } from '~/store/boundaryGroups/boundaryGroups.selectors';
import { useGetBoundaryTerritoryGroup } from '~/store/boundaryTerritoryGroups/boundaryTerritoryGroups.selectors';
import {
  boundarySelectEditCloseModal, boundarySelectEditCustomBoundaryOpenModal,
  boundarySelectQuickEditCustomBoundaryOpenModal, boundarySelectQuickEditSubmitted,
} from '~/store/frontendState/mapTools/boundary/boundarySelect/boundarySelect.actionCreators';
import { hexColorToBucketColorInfo } from '~/store/frontendState/mapTools/boundary/boundarySelect/boundarySelect.utils';
import { mapSettingsBoundariesClearIsolatedBoundaries } from '~/store/mapSettings/boundaries/mapSettingsBoundaries.actionCreators';
import { clearBoundaryLocationsFilter } from '~/store/mapSettings/toolsState/boundary/mapSettingsToolsStateBoundary.actionCreators';
import { sortNumbersAsc } from '../utils/sort';

export const useTerritoryEdit = (boundaryTerritoryGroupId: number) => {
  const dispatch = useDispatch();
  const { toggleBoundaryPolygonEdit } = useBoundaryEditPolygon();
  const { getBoundaryGroupById } = useGetBoundaryGroup();
  const { onBoundarySelectEditCustomBoundarySubmit } = useBoundarySelectEditCustomBoundary();
  const { getBoundaryTerritoryGroupById } = useGetBoundaryTerritoryGroup();
  const boundaryTerritoryGroup = getBoundaryTerritoryGroupById(boundaryTerritoryGroupId);
  const { sortedBoundaryList } = useBoundaryItems({ boundaryTerritoryGroup });
  const { openModal: showLoader, closeModal: hideLoader } = useModal('Loader');
  const boundaryGroup = getBoundaryGroupById(boundaryTerritoryGroup?.boundaryGroupId);
  const isQuickEditAllowed = (sortedBoundaryList.length <= QUICK_EDIT_BOUNDARY_COUNT_THRESHOLD);

  return useCallback((boundaryId: number) => {
    const sourceBoundaryGroupId = [...(boundaryGroup?.createdFrom?.boundaryGroupIds ?? [])].sort(sortNumbersAsc)[0];
    const sourceBoundaryGroup = getBoundaryGroupById(sourceBoundaryGroupId);

    if (!boundaryTerritoryGroup || !boundaryGroup) {
      return;
    }

    dispatch(clearBoundaryLocationsFilter());
    dispatch(mapSettingsBoundariesClearIsolatedBoundaries());

    if (sourceBoundaryGroup) {
      if (isQuickEditAllowed) {
        dispatch(boundarySelectQuickEditCustomBoundaryOpenModal({
          boundaryTerritoryGroupId: boundaryTerritoryGroup.boundaryTerritoryGroupId,
          buckets: sortedBoundaryList.map(({ id, displayName }) => {
            const bucketStyle = getBoundaryStyle(boundaryTerritoryGroup, id);

            return {
              bucketId: id,
              name: displayName,
              colorInfo: hexColorToBucketColorInfo(bucketStyle.color),
            };
          }),
          preselectedBucketId: boundaryId,
          selectBoundaryGroupId: sourceBoundaryGroup.id,
          onClose: () => {
            dispatch(boundarySelectEditCloseModal());
          },
          onSubmit: (selectState) => {
            showLoader();

            dispatch(boundarySelectQuickEditSubmitted({
              selectState,
              baseBoundaryGroupId: boundaryTerritoryGroup.boundaryGroupId,
              onDone: () => {
                dispatch(boundarySelectEditCloseModal());
                hideLoader();
              },
            }));
          },
        }));
        return;
      }
      dispatch(boundarySelectEditCustomBoundaryOpenModal({
        actionType: BoundaryEditAction.EditBoundaries,
        boundaryTerritoryGroupId,
        selectBoundaryGroupId: sourceBoundaryGroup.id,
        boundaryId,
        onClose: () => {
          dispatch(boundarySelectEditCloseModal());
        },
        onSubmit: (selectState) => {
          onBoundarySelectEditCustomBoundarySubmit(selectState);
        },
      }));
      return;
    }

    toggleBoundaryPolygonEdit(boundaryId, boundaryGroup.id, boundaryTerritoryGroupId);
  }, [boundaryGroup, boundaryTerritoryGroup, boundaryTerritoryGroupId, dispatch, getBoundaryGroupById, hideLoader,
    isQuickEditAllowed, onBoundarySelectEditCustomBoundarySubmit, showLoader, sortedBoundaryList, toggleBoundaryPolygonEdit]);
};
